import { Button, ButtonGroup } from "@blueprintjs/core";
import { useGridState, useSearchApi, IDataTableColumn, showError, DataTable, FieldSet } from "nsitools-react";
import React from "react";
import { ReprintPedigreeApi, ReprintPuppiesSearch } from "../../../api";
import { PageBase } from "../../../components";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IReprintPedigreeListProps {}

export const ReprintPedigreeList: React.FunctionComponent<IReprintPedigreeListProps> = (props) => {
  const api = useApiService(ReprintPedigreeApi);
  const { t, tUnsafe } = useTl();
  const [reprinting, setReprinting] = React.useState(false);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    availablePageSizes: [5, 15, 25],
    pageSize: 25,
  });

  const searchFunction = React.useCallback(
    (sObj?: ReprintPuppiesSearch) => {
      return api.baseSearch(sObj);
    },
    [api],
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "dogName",
      },
      {
        header: () => t(ETLCodes.Identifier),
        fieldName: "dogId",
      },
      {
        header: () => t(ETLCodes.CreatedOn),
        fieldName: "createdOn",
      },
    ],
    [t],
  );

  const reprintPedigrees = React.useCallback(async () => {
    try {
      setReprinting(true);
      await api.reprintPedigrees();
      search();
      setReprinting(false);
    } catch (err) {
      showError(tUnsafe(err));
      setReprinting(false);
    }
  }, [api, search, tUnsafe]);

  return (
    <PageBase withCard={true} breadCrumbs={[{ text: t(ETLCodes.PedigreesToReprint) }]}>
      <FieldSet
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <ButtonGroup>
              <Button
                icon={<i className="fas fa-print" />}
                text={t(ETLCodes.ReprintFirst200)}
                intent={"primary"}
                onClick={() => reprintPedigrees()}
                loading={reprinting}
              />
            </ButtonGroup>
          </div>
        }
        rightElement={
          <>
            {tableState.totalCount > 1
              ? t(ETLCodes.TableResults_Plural, { count: tableState.totalCount })
              : t(ETLCodes.TableResults, { count: tableState.totalCount })}
          </>
        }
      >
        <DataTable dateFormat="DD/MM/YYYY" tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
    </PageBase>
  );
};
